import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  faClose, faShoppingBasket, faPlus, faCubes, faCircleCheck, faCircleXmark, faTableList, faCoins,
  faBan, faTriangleExclamation, faArrowRight, faIdCard, faNotesMedical, faTag,
  faChevronLeft, faChevronRight, faChevronDown, faHospital, faBell,
} from '@fortawesome/free-solid-svg-icons';
import { formatPrice } from '../../utils/FormatPrice';
import BarcodeComponent from '../BarcodeComponent';
import LaboratoryLogo from '../Laboratory/LaboratoryLogo';
import SectionCard from '../SectionCard/SectionCard';
import { SECTION_ICON_STYLE } from '../SectionCard/sectionIconStyle';
import { transliterateToArabic } from '../../utils/transliterateArabic';
import { useAuth } from '../../context/AuthProvider';
import './ProductQuickView.scss';

const TRUNCATE_AT = 180;

const getStatusLabel = (status, t) => {
  if (status === 'MARKETED') return t('Marketed');
  if (status === 'NOT_MARKETED') return t('Not marketed');
  return status;
};

const Badge = ({ label, variant = 'info', icon }) => (
  <span className={`qv-badge qv-badge--${variant}`}>
    {icon && <FontAwesomeIcon icon={icon} />}
    {label}
  </span>
);

const getBadges = (product, t) => {
  const badges = [];

  if (product.status) {
    const isActive = String(product.status).toUpperCase() === 'MARKETED';
    badges.push({
      label: getStatusLabel(product.status, t),
      variant: isActive ? 'success' : 'neutral',
      icon: isActive ? faCircleCheck : faCircleXmark,
    });
  }

  if (product.atcCode) {
    badges.push({ label: `ATC: ${product.atcCode}`, variant: 'info', icon: faTag });
  }

  if (product.tableLetter) {
    badges.push({ label: `${t('product.table')} ${product.tableLetter}`, variant: 'warning', icon: faTableList });
  }

  if (product.psychoactiveSubstance && product.psychoactiveSubstance !== '--') {
    badges.push({ label: t('product.psychoactive_substance'), variant: 'danger', icon: faTriangleExclamation });
  }

  if (product.refundable === true) {
    badges.push({ label: t('Refundable'), variant: 'info', icon: faCoins });
  } else if (product.refundable === false) {
    badges.push({ label: t('product.non_refundable'), variant: 'neutral', icon: faBan });
  }

  return badges;
};

// Single-line, horizontally scrollable badge strip with back/next arrows —
// keeps the row from wrapping onto multiple lines when there are many badges.
const BadgeScroller = ({ badges }) => {
  const rowRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = rowRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  useEffect(() => {
    updateScrollState();
    const el = rowRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [badges.length]);

  const scrollBy = (delta) => rowRef.current?.scrollBy({ left: delta, behavior: 'smooth' });

  return (
    <div className="qv-badge-scroll">
      {canScrollLeft && (
        <button type="button" className="qv-badge-arrow qv-badge-arrow--left" onClick={() => scrollBy(-100)}>
          <span className="qv-badge-arrow-dot"><FontAwesomeIcon icon={faChevronLeft} /></span>
        </button>
      )}
      <div className="qv-badge-row" ref={rowRef}>
        {badges.map((b, i) => <Badge key={i} label={b.label} variant={b.variant} icon={b.icon} />)}
      </div>
      {canScrollRight && (
        <button type="button" className="qv-badge-arrow qv-badge-arrow--right" onClick={() => scrollBy(100)}>
          <span className="qv-badge-arrow-dot"><FontAwesomeIcon icon={faChevronRight} /></span>
        </button>
      )}
    </div>
  );
};

// "--" is the catalog's placeholder for "not filled in" — treated as empty like null/''
const isEmpty = (value) => value == null || value === '' || value === '--';

// Short field: label above, value below. Skipped when empty — a quick view only lists what's known.
const Row = ({ label, children, value }) => {
  if (children == null && isEmpty(value)) return null;
  return (
    <div className="qv-field-row">
      <span className="qv-field-label">{label}</span>
      <span className="qv-field-value">{children ?? value}</span>
    </div>
  );
};

// Long text field (same layout), with a truncate/see-more toggle. Skipped when empty.
const Block = ({ label, value, danger = false }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (isEmpty(value)) return null;
  const isLong = typeof value === 'string' && value.length > TRUNCATE_AT;

  return (
    <div className="qv-field-block">
      <span className="qv-field-block-label">{label}</span>
      <span className={`qv-field-block-value ${danger ? 'qv-field-block-value--danger' : ''}`}>
        {isLong && !expanded ? (
          <>
            {value.slice(0, TRUNCATE_AT)}{'… '}
            <button type="button" className="qv-see-more" onClick={() => setExpanded(true)}>{t('product.see_more')}</button>
          </>
        ) : (
          <>
            {value}
            {isLong && (
              <>{' '}<button type="button" className="qv-see-more" onClick={() => setExpanded(false)}>{t('product.see_less')}</button></>
            )}
          </>
        )}
      </span>
    </div>
  );
};

const ProductQuickView = ({ product, onClose, onAddToCart, addingToCart, onAddStock, onViewStock }) => {
  const { t, i18n } = useTranslation();
  const { settings, hasRight } = useAuth();
  const canEdit = hasRight('PRODUCTS_EDIT');
  const isArabic = i18n.language === 'ar';
  const panelRef = useRef(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [clinicalOpen, setClinicalOpen] = useState(false);
  // Mirrors StockAlertService's effectiveThreshold on the backend: a product
  // only overrides the pharmacy-wide default once it has its own positive
  // threshold — 0/null/undefined all mean "use the global setting".
  const globalStockAlertThreshold = Number(settings?.stock_inventory_min_stock_alert) || 10;
  const effectiveStockAlertThreshold = product.minimumStock > 0 ? product.minimumStock : globalStockAlertThreshold;

  useEffect(() => {
    setIsAnimating(false);
    setClinicalOpen(false);
    const timer = setTimeout(() => setIsAnimating(true), 10);
    return () => clearTimeout(timer);
  }, [product?.id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  if (!product) return null;

  const arText = (arValue, frValue) => (isArabic ? (arValue || transliterateToArabic(frValue)) : frValue);
  const displayName = arText(product.nameAr, product.name);
  const badges = getBadges(product, t);
  const qty = product.totalStock ?? 0;
  const noHistory = qty <= 0 && !product.hasStockHistory;
  const stockColor = qty > 3 ? '#059669' : qty > 0 ? '#d97706' : '#dc2626';
  const composition = arText(product.compositionAr, product.composition);
  const clinicalFields = [
    product.indication, product.dosagesAdministration, product.contraindication,
    product.warnings, product.dependanceRisk, product.psychoactiveSubstance,
  ];
  const hasClinicalInfo = clinicalFields.some((v) => !isEmpty(v));

  return (
    <>
      <div className={`qv-backdrop ${isAnimating ? 'qv-backdrop--open' : ''}`} onClick={onClose} />
      <div ref={panelRef} className={`qv-panel ${isAnimating ? 'qv-panel--open' : 'qv-panel--closed'}`}>
      <button type="button" onClick={onClose} className="qv-close">
        <FontAwesomeIcon icon={faClose} />
      </button>

      <div className="qv-scroll">
        <div className="qv-header">
          <img
            className="qv-image"
            src={product.images?.length > 0 ? product.images[0].url : '/images/no_product.png'}
            alt={displayName}
          />
          <div className="qv-header-info">
            <p className="qv-name" dir={isArabic ? 'rtl' : undefined}>{displayName}</p>
            <p className="qv-sub">
              {[arText(product.presentationAr, product.presentation), arText(product.dosageAr, product.dosage)].filter(Boolean).join(' · ')}
            </p>
            {product.laboratory?.name && (
              <NavLink to={`/suppliers/${product.laboratory.id}`} className="qv-lab" onClick={(e) => e.stopPropagation()}>
                <LaboratoryLogo id={product.laboratory.id} name={product.laboratory.name} size={16} />
                {product.laboratory.name}
              </NavLink>
            )}
            {!isEmpty(composition) && <p className="qv-dci">{composition}</p>}
          </div>
        </div>

        {badges.length > 0 && <BadgeScroller badges={badges} />}

        {product.expiredBatchCount > 0 && (
          <div className="qv-alert">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <span>{product.expiredBatchCount} {product.expiredBatchCount > 1 ? 'lots périmés' : 'lot périmé'}</span>
          </div>
        )}

        {/* Key numbers first: what the counter needs at a glance */}
        <div className="qv-stats">
          <div className="qv-stat-card">
            <span className="qv-stat-card__icon" style={SECTION_ICON_STYLE}>
              <FontAwesomeIcon icon={faCoins} />
            </span>
            <div>
              <div className="qv-stat-label">{t('product.public_price')}</div>
              <div className="qv-stat-value">{formatPrice(product.publicPrice)}</div>
            </div>
          </div>
          <div className="qv-stat-card">
            <span className="qv-stat-card__icon" style={SECTION_ICON_STYLE}>
              <FontAwesomeIcon icon={faHospital} />
            </span>
            <div>
              <div className="qv-stat-label">{t('product.hospital_price')}</div>
              {product.hospitalPrice
                ? <div className="qv-stat-value">{formatPrice(product.hospitalPrice)}</div>
                : <div className="qv-stat-value qv-stat-value--muted">—</div>}
            </div>
          </div>
          <div className="qv-stat-card">
            <span className="qv-stat-card__icon" style={SECTION_ICON_STYLE}>
              <FontAwesomeIcon icon={faCubes} />
            </span>
            <div>
              <div className="qv-stat-label">{t('product.available_stock')}</div>
              {noHistory ? (
                <div className="qv-stat-value qv-stat-value--muted">—</div>
              ) : (
                <div className="qv-stat-value" style={{ color: stockColor }}>{qty}</div>
              )}
            </div>
          </div>
          <div className="qv-stat-card">
            <span className="qv-stat-card__icon" style={SECTION_ICON_STYLE}>
              <FontAwesomeIcon icon={faBell} />
            </span>
            <div>
              <div className="qv-stat-label">{t('product.stock_alert_threshold')}</div>
              <div className="qv-stat-value">{effectiveStockAlertThreshold}</div>
            </div>
          </div>
        </div>

        {/* Only what the header and badges don't already show (name, presentation, dosage,
            lab, status, refundable, ATC, tableau are all up there) */}
        {[product.family, product.productType, product.barcode].some((v) => !isEmpty(v)) && (
          <SectionCard title={t('product.identification')} icon={faIdCard} className="qv-section">
            <Row label={t('product.family')} value={arText(product.familyAr, product.family)} />
            <Row label={t('product.product_type')} value={product.productType} />
            {!isEmpty(product.barcode) && (
              <Row label={t('Barcode')}>
                <BarcodeComponent value={product.barcode} format="EAN13" height={30} fontSize={11} />
              </Row>
            )}
          </SectionCard>
        )}

        {/* Long clinical texts: collapsed so they don't push everything else down */}
        {hasClinicalInfo && (
          <div className="qv-section qv-clinical">
            <button
              type="button"
              className="qv-clinical__toggle"
              onClick={() => setClinicalOpen((open) => !open)}
              aria-expanded={clinicalOpen}
            >
              <span className="qv-clinical__icon" style={SECTION_ICON_STYLE}>
                <FontAwesomeIcon icon={faNotesMedical} />
              </span>
              {t('product.clinical_info')}
              <FontAwesomeIcon icon={faChevronDown} className={`qv-clinical__chevron${clinicalOpen ? ' is-open' : ''}`} />
            </button>
            {clinicalOpen && (
              <div className="qv-clinical__body">
                <Block label={t('product.indication')} value={arText(product.indicationAr, product.indication)} />
                <Block label={t('product.posology')} value={arText(product.dosagesAdministrationAr, product.dosagesAdministration)} />
                <Block
                  label={t('product.contraindications')}
                  value={arText(product.contraindicationAr, product.contraindication)}
                  danger={!isEmpty(product.contraindication)}
                />
                <Block label={t('product.warnings')} value={arText(product.warningsAr, product.warnings)} />
                <Block label={t('product.dependence')} value={product.dependanceRisk} />
                <Block label={t('product.psychoactive_substance')} value={product.psychoactiveSubstance} />
              </div>
            )}
          </div>
        )}

        {product.images?.length > 0 && (
          <div className="qv-images-strip">
            {product.images.map((img, i) => (
              <img key={i} src={img.url} alt={`${product.name} ${i + 1}`} className="qv-images-strip__img" />
            ))}
          </div>
        )}

        <NavLink to={`/products/${product.id}`} className="qv-full-link">
          {t('View product')}
          <FontAwesomeIcon icon={faArrowRight} />
        </NavLink>
      </div>

      <div className="qv-actions">
        {canEdit && (
          <button type="button" className="qv-action-btn" onClick={() => onAddStock(product.id)}>
            <FontAwesomeIcon icon={faPlus} />
            {t('Add to stock')}
          </button>
        )}
        <button type="button" className="qv-action-btn" onClick={() => onViewStock(product.id, product.name)}>
          <FontAwesomeIcon icon={faCubes} />
          {t('View batches')}
        </button>
        <button
          type="button"
          className="qv-action-btn qv-action-btn--primary"
          onClick={() => onAddToCart(product.id)}
          disabled={addingToCart}
        >
          {addingToCart ? <span className="spinner-border spinner-border-sm" /> : <FontAwesomeIcon icon={faShoppingBasket} />}
          {t('Add to cart')}
        </button>
      </div>
      </div>
    </>
  );
};

export default ProductQuickView;
